import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { incidentsApi } from '../../../functions/IncidentsApi';

/**
 * WHY A PORTAL?
 * The React app mounts into <div id="root"> — a direct child of <body>.
 * @media print hides #root to clear the screen. If this component lived
 * inside #root it would be hidden too.
 *
 * By portaling to a new <div id="incident-print-view"> appended to <body>,
 * it becomes a sibling of #root and survives the hide rule.
 *
 *   <body>
 *     <div id="root">                 ← hidden by @media print
 *     <div id="incident-print-view">  ← shown full-page
 *
 * NOTE: IncidentPrintView must NOT also be rendered inside ViewIncidentModal.
 * This component is rendered once at the Incidents.jsx page level only.
 */

/* ─── helpers ──────────────────────────────────────────────────────── */

const fmt = (dt) => (dt ? moment(dt).format('MMM D, YYYY  HH:mm:ss') : '—');

const duration = (start, end) => {
  if (!start || !end) return '—';
  const diff = moment(end).diff(moment(start), 'seconds');
  if (diff < 0) return '—'; // known CAD timestamp issue
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
};

/** Turn SCREAMING_SNAKE or pipe-delimited enums into readable text */
const humanize = (str) => {
  if (!str || typeof str !== 'string') return '—';
  return str
    .replace(/\|\|/g, ' › ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Check if a value is "empty" */
const isEmpty = (v) => {
  if (v === null || v === undefined || v === '' || v === '—') return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return false;
};

/** Fields to skip when auto-rendering */
const SKIP_KEYS = new Set([
  'id', 'neris_uid', 'incident_id', 'neris_presence_uid',
  'operation_id', 'fire_details', 'fire_detaile_id',
  'casualty_rescue', 'dispatch_id', 'action_id',
  'hazsit_detail_id', 'fire_suppression_id',
  'street_id', 'neris_dispatch_id', 'incident',
]);

/** Check if an object has any displayable data */
const hasData = (obj) => {
  if (!obj) return false;
  if (Array.isArray(obj)) return obj.length > 0;
  if (typeof obj !== 'object') return true;
  return Object.entries(obj).some(
    ([k, v]) => !SKIP_KEYS.has(k) && !isEmpty(v)
  );
};

const formatValue = (val) => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'number') return val.toLocaleString('en-US');
  if (typeof val === 'string') {
    if (/^\d{4}-\d{2}-\d{2}[T ]/.test(val)) return fmt(val);
    return humanize(val);
  }
  if (Array.isArray(val)) {
    if (val.length === 0) return '—';
    if (typeof val[0] === 'string') return val.map(humanize).join(', ');
  }
  return String(val);
};

/**
 * Build a two-line address:
 *   Line 1: unit_value + street + street_postfix_modifier
 *   Line 2: postal_community, state  postal_code
 */
const buildAddress = (location) => {
  if (!location) return { line1: '—', line2: '' };

  // Handle both nested street object and flat street fields
  const streetObj = location.street || {};
  const streetName = streetObj.street || location.street_name || '';
  const streetNumber = streetObj.complete_number || streetObj.number || '';

  const line1Parts = [
    streetNumber,
    location.unit_value,
    streetName || location.street_postfix_modifier,
  ].filter(Boolean);

  const cityState = [location.postal_community, location.state]
    .filter(Boolean)
    .join(', ');
  const zip = location.postal_code || '';
  const line2 = [cityState, zip].filter(Boolean).join('  ');

  return {
    line1: line1Parts.join(' ') || '—',
    line2,
  };
};

/* ─── layout primitives ───────────────────────────────────────────── */

const Section = ({ title, children }) => (
  <div style={styles.section}>
    <div style={styles.sectionHeader}>{title}</div>
    {children}
  </div>
);

const SubHeader = ({ children }) => (
  <div style={styles.subHeader}>{children}</div>
);

const Row = ({ label, value }) => {
  if (isEmpty(value)) return null;
  return (
    <div style={styles.row}>
      <span style={styles.label}>{label}</span>
      <span style={styles.value}>
        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value ?? '—')}
      </span>
    </div>
  );
};

/** Auto-render key-value pairs from an object, skipping internal IDs */
const AutoRows = ({ data }) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return (
    <>
      {Object.entries(data)
        .filter(([k]) => !SKIP_KEYS.has(k))
        .filter(([, v]) => !isEmpty(v) && typeof v !== 'object')
        .map(([k, v]) => (
          <Row key={k} label={humanize(k)} value={formatValue(v)} />
        ))}
    </>
  );
};

/** Render a table from an array of objects */
const AutoTable = ({ data, columns }) => {
  if (!data || data.length === 0) return null;
  const cols = columns ||
    Object.keys(data[0]).filter((k) => !SKIP_KEYS.has(k));
  return (
    <table style={styles.table}>
      <thead>
        <tr>
          {cols.map((c) => (
            <th key={typeof c === 'string' ? c : c.key} style={styles.th}>
              {typeof c === 'string' ? humanize(c) : c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={i}>
            {cols.map((c) => {
              const key = typeof c === 'string' ? c : c.key;
              return (
                <td key={key} style={styles.td}>
                  {formatValue(row[key])}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/* ─── resolve data from either base.X or top-level X ─────────────── */

const resolve = (incident, key) => {
  if (!incident) return undefined;
  // Check base first (production format), then top-level (toForm format)
  if (incident.base && incident.base[key] !== undefined) return incident.base[key];
  if (incident[key] !== undefined) return incident[key];
  return undefined;
};

/* ─── main component ──────────────────────────────────────────────── */

export default function IncidentPrintView({ incident, department, onReady }) {
  const [portalEl, setPortalEl] = useState(null);
  const [propertyDamages, setPropertyDamages] = useState(null);
  const [notes, setNotes] = useState(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.id = 'incident-print-view';
    document.body.appendChild(el);
    setPortalEl(el);
    return () => {
      if (document.body.contains(el)) document.body.removeChild(el);
    };
  }, []);

  useEffect(() => {
    if (!incident?.id) return;
    setPropertyDamages(null);
    setNotes(null);

    const fetches = [
      incidentsApi.getPropertyDamage(incident.id)
        .then((res) => {
          const data = res.status === 200 ? (res.data || []) : [];
          setPropertyDamages(data);
        })
        .catch(() => setPropertyDamages([])),
    ];

    if (incidentsApi.getNotes) {
      fetches.push(
        incidentsApi.getNotes(incident.id)
          .then((res) => {
            if (res.status === 200) setNotes(res.data || []);
          })
          .catch(() => {})
      );
    }

    Promise.all(fetches).then(() => {
      // Let React paint the updated state before signaling ready to print
      setTimeout(() => { if (onReady) onReady(); }, 50);
    });
  }, [incident?.id]);

  if (!portalEl) return null;

  const content = !incident ? null : (() => {
    const base = incident.base || {};

    /* ── core fields (matching prod data shape) ── */
    const d                = resolve(incident, 'dispatch') || {};
    const unit_responses   = resolve(incident, 'unit_responses') || [];
    const incident_types   = resolve(incident, 'incident_types') || base.incident_types || [];
    const casualty_rescues = resolve(incident, 'casualty_rescues') || [];
    const noteField        = resolve(incident, 'note');
    const status           = resolve(incident, 'status') || base.call_status;
    const neris_status     = resolve(incident, 'neris_status');
    const location         = resolve(incident, 'location') || {};
    const location_use     = resolve(incident, 'location_use');
    const actions_tactics  = resolve(incident, 'actions_tactics');

    /* ── new data fields ── */
    const tactic_timestamps       = resolve(incident, 'tactic_timestamps');
    const exposures               = resolve(incident, 'exposures') || [];
    const aids                    = resolve(incident, 'aids') || [];
    const casualties              = resolve(incident, 'casualty') || casualty_rescues;
    const rescues                 = resolve(incident, 'rescues') || [];
    const medical_details         = resolve(incident, 'medical_detail') || [];
    const fire_detail             = resolve(incident, 'fire_detail');
    const hazsit_detail           = resolve(incident, 'hazsit_detail');
    const smoke_alarm             = resolve(incident, 'smoke_alarm');
    const fire_alarm              = resolve(incident, 'fire_alarm');
    const other_alarm             = resolve(incident, 'other_alarm');
    const fire_suppression        = resolve(incident, 'fire_suppression');
    const cooking_fire_suppression = resolve(incident, 'cooking_fire_suppression');
    const electric_hazards        = resolve(incident, 'electric_hazards') || [];
    const powergen_hazards        = resolve(incident, 'powergen_hazards') || [];
    const csst_hazard             = resolve(incident, 'csst_hazard');
    const medical_oxygen_hazard   = resolve(incident, 'medical_oxygen_hazard');

    const actions = actions_tactics?.action_noaction?.actions
      ?? actions_tactics?.actions ?? [];

    const address = buildAddress(location);
    const incidentNumber = d?.incident_number ?? base?.incident_number ?? '—';
    const primaryType = base?.primary_incident_type
      || incident_types?.find?.((t) => t.primary)
      || incident_types?.[0];
    const oic = incident.name
      ? `${incident.name}${incident.rank ? ` (${incident.rank})` : ''}`
      : (base?.responsible_officer ?? '—');

    const totalPropertyDamage = propertyDamages?.reduce(
      (sum, p) => sum + (parseFloat(p.amount) || 0), 0
    ) ?? 0;

    const printedAt = moment().format('MMM D, YYYY HH:mm');

    const fmtUseType = (raw) =>
      raw ? raw.replace(/\|\|/g, ' › ').replace(/_/g, ' ') : '—';

    const fmtAction = (raw) =>
      raw ? raw.replace(/\|\|/g, ' › ').replace(/_/g, ' ') : raw;

    /* ── Section numbering — auto-increment, skip sections with no data ── */
    let sectionNum = 0;
    const nextSection = () => ++sectionNum;

    return (
      <div style={styles.page}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={styles.title}>{(department || 'Fire Department').replace(/\s*Local\s*\d+/gi, '').trim()}</div>
            <div style={styles.title}>NERIS Incident Report</div>
            <div style={styles.subtitle}>Incident #{incidentNumber}</div>
          </div>
          <div style={styles.meta}>
            <div>Dept NERIS ID: {base?.department_neris_id ?? '—'}</div>
            <div>Printed: {printedAt}</div>
          </div>
        </div>

        {/* ── Incident Identification ── */}
        <Section title={`${nextSection()}. Incident Identification`}>
          <Row label="Incident Number"   value={incidentNumber} />
          <Row label="CAD Type"          value={base?.cad_type ?? '—'} />
          <Row label="CAD Code"          value={base?.cad_type_code ?? '—'} />
          <Row label="Call Status"       value={status ?? '—'} />
          <Row label="NERIS Status"      value={neris_status ?? '—'} />
          <Row
            label="Primary Type"
            value={primaryType
              ? (typeof primaryType === 'string' ? humanize(primaryType) : humanize(primaryType.type))
              : '—'}
          />
          {incident_types && incident_types.length > 1 && (
            <Row
              label="Additional Types"
              value={incident_types
                .filter((t) => !t.primary)
                .map((t) => humanize(t.type || t))
                .join(', ')}
            />
          )}
          {base?.special_modifiers?.length > 0 && (
            <Row label="Special Modifiers" value={
              (Array.isArray(base.special_modifiers)
                ? base.special_modifiers.map((s) => typeof s === 'string' ? humanize(s) : humanize(s.name || '')).join(', ')
                : humanize(base.special_modifiers))
            } />
          )}
          {base?.nonfd_aids?.length > 0 && (
            <Row label="Non-FD Aids" value={
              base.nonfd_aids.map((a) => typeof a === 'string' ? humanize(a) : humanize(a.name || '')).join(', ')
            } />
          )}
          <Row label="Officer in Charge" value={oic} />
          <div style={styles.row}>
            <span style={styles.label}>Address</span>
            <span style={styles.value}>
              {address.line1}
              {address.line2 && <><br />{address.line2}</>}
              {location?.section && <><br /><span style={{ color: '#555' }}>{location.section}</span></>}
            </span>
          </div>
        </Section>

        {/* ── Location Details ── */}
        {hasData(location) && (location.place_type || location.county || location.structure
          || location.floor || location.wing || location.room || location.marker
          || location.additional_info || location.cross_streets?.length > 0) && (
          <Section title={`${nextSection()}. Location Details`}>
            <Row label="Place Type" value={humanize(location.place_type)} />
            <Row label="Municipality" value={location.incorporated_municipality} />
            <Row label="County" value={location.county} />
            <Row label="Neighborhood" value={location.neighborhood_community} />
            <Row label="Structure" value={location.structure} />
            <Row label="Site" value={location.site} />
            <Row label="Subsite" value={location.subsite} />
            <Row label="Wing" value={location.wing} />
            <Row label="Floor" value={location.floor} />
            <Row label="Unit" value={
              (location.unit_prefix || location.unit_value)
                ? `${location.unit_prefix || ''} ${location.unit_value || ''}`.trim()
                : null
            } />
            <Row label="Room" value={location.room} />
            <Row label="Row" value={location.row} />
            <Row label="Seat" value={location.seat} />
            <Row label="Marker" value={location.marker} />
            <Row label="Additional Info" value={location.additional_info} />
            {location.additional_attributes?.length > 0 && (
              <Row label="Additional Attributes" value={
                location.additional_attributes
                  .map((a) => typeof a === 'object' ? a.attribute : a)
                  .filter(Boolean)
                  .join(', ')
              } />
            )}
            {location.cross_streets?.length > 0 && (
              <>
                <SubHeader>Cross Streets</SubHeader>
                {location.cross_streets.map((cs, i) => (
                  <Row key={i} label={`Cross Street ${i + 1}`} value={
                    [cs.street_prefix_direction, cs.street, cs.street_postfix_direction]
                      .filter(Boolean).join(' ') || '—'
                  } />
                ))}
              </>
            )}
            {location.point && hasData(location.point) && (
              <>
                <SubHeader>Coordinates</SubHeader>
                <AutoRows data={location.point} />
              </>
            )}
          </Section>
        )}

        {/* ── Location Use ── */}
        {location_use && hasData(location_use) && (
          <Section title={`${nextSection()}. Location Use`}>
            <Row label="In Use" value={
              location_use.in_use != null
                ? (typeof location_use.in_use === 'object'
                    ? (location_use.in_use.in_use ? 'Yes' : 'No')
                    : (location_use.in_use ? 'Yes' : 'No'))
                : null
            } />
            <Row label="Use Type" value={fmtUseType(location_use.use_type)} />
            <Row label="Secondary Use" value={fmtUseType(location_use.secondary_use)} />
            <Row label="Vacancy Cause" value={location_use.vacancy_cause} />
          </Section>
        )}

        {/* ── Dispatch & Response Times ── */}
        <Section title={`${nextSection()}. Dispatch & Response Times`}>
          <Row label="Call Created"     value={fmt(d?.call_create)} />
          <Row label="Call Answered"    value={fmt(d?.call_answered)} />
          <Row label="Call Arrival"     value={fmt(d?.call_arrival)} />
          <Row label="Incident Cleared" value={fmt(d?.incident_clear || d?.incident_cleared)} />
          <Row
            label="Response Time (create → arrival)"
            value={duration(d?.call_create, d?.call_arrival)}
          />
          <Row
            label="Total Incident Duration"
            value={duration(d?.call_create, d?.incident_clear || d?.incident_cleared)}
          />
          <Row label="Center ID" value={d?.center_id} />
          <Row label="Determinant Code" value={d?.determinant_code} />
          <Row label="Incident Code" value={d?.incident_code} />
          <Row label="Disposition" value={d?.disposition} />
          <Row label="Automatic Alarm" value={d?.automatic_alarm} />
          {d?.comments && (
            Array.isArray(d.comments) ? (
              <>
                <SubHeader>Dispatch Comments</SubHeader>
                {d.comments.map((c, i) => (
                  <Row key={i} label={`Comment ${i + 1}`} value={
                    typeof c === 'string' ? c : (c.comment || c.text || JSON.stringify(c))
                  } />
                ))}
              </>
            ) : (
              <Row label="Dispatch Comments" value={d.comments} />
            )
          )}
        </Section>

        {/* ── Assigned Apparatus ── */}
        <Section title={`${nextSection()}. Assigned Apparatus`}>
          {!unit_responses || unit_responses.length === 0 ? (
            <div style={styles.none}>No units recorded.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Unit', 'Dispatched', 'Enroute', 'On Scene', 'Staging', 'Cleared'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {unit_responses.map((u, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{u.reported_unit_id ?? '—'}</td>
                    <td style={styles.td}>{fmt(u.dispatch)}</td>
                    <td style={styles.td}>{fmt(u.enroute_to_scene || u.enroute)}</td>
                    <td style={styles.td}>{fmt(u.on_scene)}</td>
                    <td style={styles.td}>{fmt(u.staging)}</td>
                    <td style={styles.td}>{fmt(u.unit_clear)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        {/* ── Tactic Timestamps ── */}
        {tactic_timestamps && hasData(tactic_timestamps) && (
          <Section title={`${nextSection()}. Tactic Timestamps`}>
            <AutoRows data={tactic_timestamps} />
          </Section>
        )}

        {/* ── Actions & Tactics ── */}
        {(actions.length > 0 || (actions_tactics && (actions_tactics.type || actions_tactics.noaction_type || actions_tactics.action_noaction?.type))) && (
          <Section title={`${nextSection()}. Actions & Tactics`}>
            <Row label="Action Type" value={actions_tactics?.type || actions_tactics?.action_noaction?.type} />
            <Row label="No-Action Reason" value={actions_tactics?.noaction_type || actions_tactics?.action_noaction?.noaction_type} />
            {actions.length > 0 && (
              <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
                {actions.map((a, i) => (
                  <li key={i} style={{ fontSize: 12, padding: '2px 0' }}>{fmtAction(typeof a === 'string' ? a : (a.action || a.type || JSON.stringify(a)))}</li>
                ))}
              </ul>
            )}
          </Section>
        )}

        {/* ── Outcome & Narrative ── */}
        {(base?.outcome_narrative || base?.impediment_narrative ||
          base?.people_present != null || base?.animals_rescued != null ||
          base?.displacement_count != null) && (
          <Section title={`${nextSection()}. Outcome & Narrative`}>
            {base?.outcome_narrative && (
              <div style={styles.row}>
                <span style={styles.label}>Outcome</span>
                <span style={{ ...styles.value, whiteSpace: 'pre-wrap' }}>{base.outcome_narrative}</span>
              </div>
            )}
            {base?.impediment_narrative && (
              <div style={styles.row}>
                <span style={styles.label}>Impediments</span>
                <span style={{ ...styles.value, whiteSpace: 'pre-wrap' }}>{base.impediment_narrative}</span>
              </div>
            )}
            {base?.people_present != null && <Row label="People Present" value={base.people_present ? 'Yes' : 'No'} />}
            {base?.animals_rescued != null && <Row label="Animals Rescued" value={base.animals_rescued} />}
            {base?.displacement_count != null && <Row label="Displaced" value={base.displacement_count} />}
            {base?.displacement_causes && hasData(base.displacement_causes) && (
              <Row label="Displacement Causes" value={
                Array.isArray(base.displacement_causes)
                  ? base.displacement_causes.map((c) =>
                      typeof c === 'string' ? humanize(c) : humanize(c.cause || c.type || '')
                    ).join(', ')
                  : humanize(base.displacement_causes)
              } />
            )}
          </Section>
        )}

        {/* ── Mutual Aid ── */}
        {aids.length > 0 && (
          <Section title={`${nextSection()}. Mutual Aid`}>
            {aids.map((a, i) => (
              <div key={i} style={{ marginBottom: aids.length > 1 ? 8 : 0 }}>
                {aids.length > 1 && <SubHeader>{`Aid ${i + 1}`}</SubHeader>}
                <Row label="Aid Type" value={humanize(a.aid_type || a.type)} />
                <Row label="Department NERIS ID" value={a.department_neris_id || a.department} />
                <Row label="Department Name" value={a.department_name} />
                <Row label="Incident Number" value={a.incident_number} />
              </div>
            ))}
          </Section>
        )}

        {/* ── Casualties ── */}
        {casualties && casualties.length > 0 && (
          <Section title={`${nextSection()}. Casualties`}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Type', 'Gender', 'Race', 'Birth Mo/Yr', 'Rank', 'Yrs of Service'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {casualties.map((c, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{c.type ? humanize(c.type) : '—'}</td>
                    <td style={styles.td}>{c.gender ? humanize(c.gender) : '—'}</td>
                    <td style={styles.td}>{c.race ? humanize(c.race) : '—'}</td>
                    <td style={styles.td}>{c.birth_month_year ?? '—'}</td>
                    <td style={styles.td}>{c.rank ?? '—'}</td>
                    <td style={styles.td}>{c.years_of_service ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Show casualty-specific details if present */}
            {casualties.some((c) => c.severity || c.cause || c.injury_type || c.activity) && (
              <>
                <SubHeader>Casualty Details</SubHeader>
                {casualties.filter((c) => c.severity || c.cause || c.injury_type || c.activity).map((c, i) => (
                  <div key={i} style={{ marginBottom: 6 }}>
                    <Row label="Severity" value={humanize(c.severity)} />
                    <Row label="Cause" value={humanize(c.cause)} />
                    <Row label="Injury Type" value={humanize(c.injury_type)} />
                    <Row label="Activity" value={humanize(c.activity)} />
                  </div>
                ))}
              </>
            )}
          </Section>
        )}

        {/* ── Rescues ── */}
        {rescues.length > 0 && (
          <Section title={`${nextSection()}. Rescues`}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Type', 'Gender', 'Race', 'Birth Mo/Yr', 'Rescued Count', 'Rescue Type'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rescues.map((r, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{r.type ? humanize(r.type) : '—'}</td>
                    <td style={styles.td}>{r.gender ? humanize(r.gender) : '—'}</td>
                    <td style={styles.td}>{r.race ? humanize(r.race) : '—'}</td>
                    <td style={styles.td}>{r.birth_month_year ?? '—'}</td>
                    <td style={styles.td}>{r.number_rescued ?? r.count ?? '—'}</td>
                    <td style={styles.td}>{r.rescue_type ? humanize(r.rescue_type) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* ── Medical Details ── */}
        {medical_details.length > 0 && (
          <Section title={`${nextSection()}. Medical Details`}>
            {medical_details.map((md, i) => (
              <div key={i} style={{ marginBottom: medical_details.length > 1 ? 12 : 0 }}>
                {medical_details.length > 1 && <SubHeader>{`Patient ${i + 1}`}</SubHeader>}
                <AutoRows data={md} />
              </div>
            ))}
          </Section>
        )}

        {/* ── Fire Detail ── */}
        {fire_detail && hasData(fire_detail) && (
          <Section title={`${nextSection()}. Fire Detail`}>
            <Row label="Water Supply" value={humanize(fire_detail.water_supply)} />
            <Row label="Investigation Needed" value={humanize(fire_detail.investigation_needed)} />
            <Row label="Fire Location Type" value={humanize(fire_detail.type)} />
            <Row label="Progression Evident" value={fire_detail.progression_evident} />
            <Row label="Floor of Origin" value={fire_detail.floor_of_origin} />
            <Row label="Arrival Condition" value={humanize(fire_detail.arrival_condition)} />
            <Row label="Damage Type" value={humanize(fire_detail.damage_type)} />
            <Row label="Room of Origin" value={humanize(fire_detail.room_of_origin_type)} />
            <Row label="Cause" value={humanize(fire_detail.cause)} />
            <Row label="Acres Burned" value={fire_detail.acres_burned} />
            {fire_detail.location_detail && hasData(fire_detail.location_detail) && (
              <>
                <SubHeader>Fire Location Detail</SubHeader>
                <AutoRows data={fire_detail.location_detail} />
              </>
            )}
            {fire_detail.investigation_types?.length > 0 && (
              <>
                <SubHeader>Investigation Types</SubHeader>
                {typeof fire_detail.investigation_types[0] === 'string' ? (
                  <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
                    {fire_detail.investigation_types.map((t, i) => (
                      <li key={i} style={{ fontSize: 12, padding: '2px 0' }}>{humanize(t)}</li>
                    ))}
                  </ul>
                ) : (
                  <AutoTable data={fire_detail.investigation_types} />
                )}
              </>
            )}
            {fire_detail.suppression_appliances?.length > 0 && (
              <>
                <SubHeader>Suppression Appliances</SubHeader>
                <AutoTable data={fire_detail.suppression_appliances} />
              </>
            )}
          </Section>
        )}

        {/* ── HazSit Detail ── */}
        {hazsit_detail && hasData(hazsit_detail) && (
          <Section title={`${nextSection()}. Hazardous Situation Detail`}>
            <Row label="Evacuated" value={hazsit_detail.evacuated} />
            <Row label="Disposition" value={humanize(hazsit_detail.disposition)} />
            {hazsit_detail.chemicals?.length > 0 && (
              <>
                <SubHeader>Chemicals</SubHeader>
                {hazsit_detail.chemicals.map((chem, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    {hazsit_detail.chemicals.length > 1 && (
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#444', marginBottom: 4 }}>
                        Chemical {i + 1}
                      </div>
                    )}
                    <AutoRows data={chem} />
                  </div>
                ))}
              </>
            )}
          </Section>
        )}

        {/* ── Smoke Alarm ── */}
        {smoke_alarm && hasData(smoke_alarm) && (
          <Section title={`${nextSection()}. Smoke Alarm`}>
            <AutoRows data={smoke_alarm.presence || smoke_alarm} />
          </Section>
        )}

        {/* ── Fire Alarm ── */}
        {fire_alarm && hasData(fire_alarm) && (
          <Section title={`${nextSection()}. Fire Alarm`}>
            <AutoRows data={fire_alarm.presence || fire_alarm} />
          </Section>
        )}

        {/* ── Other Alarm ── */}
        {other_alarm && hasData(other_alarm) && (
          <Section title={`${nextSection()}. Other Alarm`}>
            <AutoRows data={other_alarm.presence || other_alarm} />
          </Section>
        )}

        {/* ── Fire Suppression System ── */}
        {fire_suppression && hasData(fire_suppression) && (
          <Section title={`${nextSection()}. Fire Suppression System`}>
            {(() => {
              const fs = fire_suppression.presence || fire_suppression;
              return (
                <>
                  <Row label="Type" value={humanize(fs.type)} />
                  <Row label="Operation Type" value={humanize(fs.operation_type)} />
                  <Row label="Sprinklers Activated" value={fs.sprinklers_activated} />
                  <Row label="Failure Reason" value={humanize(fs.failure_reason)} />
                  {fs.suppression_types?.length > 0 && (
                    <>
                      <SubHeader>Suppression Types</SubHeader>
                      <AutoTable data={fs.suppression_types} />
                    </>
                  )}
                </>
              );
            })()}
          </Section>
        )}

        {/* ── Cooking Fire Suppression ── */}
        {cooking_fire_suppression && hasData(cooking_fire_suppression) && (
          <Section title={`${nextSection()}. Cooking Fire Suppression`}>
            <AutoRows data={cooking_fire_suppression.presence || cooking_fire_suppression} />
          </Section>
        )}

        {/* ── Electric Hazards ── */}
        {electric_hazards.length > 0 && (
          <Section title={`${nextSection()}. Electric Hazards`}>
            {electric_hazards.map((eh, i) => (
              <div key={i} style={{ marginBottom: electric_hazards.length > 1 ? 12 : 0 }}>
                {electric_hazards.length > 1 && <SubHeader>{`Hazard ${i + 1}`}</SubHeader>}
                <AutoRows data={eh} />
              </div>
            ))}
          </Section>
        )}

        {/* ── Power Generation Hazards ── */}
        {powergen_hazards.length > 0 && (
          <Section title={`${nextSection()}. Power Generation Hazards`}>
            {powergen_hazards.map((ph, i) => (
              <div key={i} style={{ marginBottom: powergen_hazards.length > 1 ? 12 : 0 }}>
                {powergen_hazards.length > 1 && <SubHeader>{`Hazard ${i + 1}`}</SubHeader>}
                <AutoRows data={ph} />
              </div>
            ))}
          </Section>
        )}

        {/* ── CSST Hazard ── */}
        {csst_hazard && hasData(csst_hazard) && (
          <Section title={`${nextSection()}. CSST Hazard`}>
            <AutoRows data={csst_hazard} />
          </Section>
        )}

        {/* ── Medical Oxygen Hazard ── */}
        {medical_oxygen_hazard && hasData(medical_oxygen_hazard) && (
          <Section title={`${nextSection()}. Medical Oxygen Hazard`}>
            <AutoRows data={medical_oxygen_hazard} />
          </Section>
        )}

        {/* ── Exposures ── */}
        {exposures.length > 0 && (
          <Section title={`${nextSection()}. Exposures`}>
            {exposures.map((exp, i) => (
              <div key={i} style={{ marginBottom: exposures.length > 1 ? 16 : 0 }}>
                {exposures.length > 1 && <SubHeader>{`Exposure ${i + 1}`}</SubHeader>}
                <AutoRows data={exp} />
              </div>
            ))}
          </Section>
        )}

        {/* ── Property Damage ── */}
        <Section title={`${nextSection()}. Property Damage`}>
          {propertyDamages === null ? (
            <div style={styles.none}>Loading…</div>
          ) : propertyDamages.length === 0 ? (
            <div style={styles.none}>No property damage recorded.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Description</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {propertyDamages.map((p, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{p.description || '—'}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      {p.amount != null
                        ? `$${parseFloat(p.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                        : '—'}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...styles.td, fontWeight: 700 }}>Total</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>
                    ${totalPropertyDamage.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </Section>

        {/* ── Notes ── */}
        {(() => {
          // Normalize notes from API (could be array, {data:[...]}, object, or string)
          const toArray = (val) => {
            if (!val) return [];
            if (Array.isArray(val)) return val;
            if (Array.isArray(val?.data)) return val.data;
            if (typeof val === 'string') return [val];
            if (typeof val === 'object') return [val];
            return [];
          };
          const allNotes = [
            ...toArray(notes),
            ...(notes ? [] : toArray(noteField)),  // only use noteField if API notes didn't load
          ];
          if (allNotes.length === 0) return null;
          return (
            <Section title={`${nextSection()}. Notes`}>
              {allNotes.map((n, i) => (
                <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                  {typeof n === 'object' ? (
                    <>
                      {(n.note || n.text) && <div style={{ color: '#111', whiteSpace: 'pre-wrap' }}>{n.note || n.text}</div>}
                      {n.created_at && (
                        <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                          {fmt(n.created_at)} {n.author ? `— ${n.author}` : ''}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: '#111', whiteSpace: 'pre-wrap' }}>{n}</div>
                  )}
                </div>
              ))}
            </Section>
          );
        })()}

        {/* Footer */}
        <div style={styles.footer}>
          Firehouse247 · NERIS Incident Report · #{incidentNumber} · {printedAt}
        </div>
      </div>
    );
  })();

  return createPortal(
    <>
      <style>{`
        @media print {
          /* React root is #root — confirmed by DOM inspection */
          #root { display: none !important; }
          #incident-print-view { display: block !important; }
        }
        #incident-print-view { display: none; }
      `}</style>
      {content}
    </>,
    portalEl
  );
}

/* ─── styles ──────────────────────────────────────────────────────── */

const styles = {
  page: {
    fontFamily: 'Arial, sans-serif',
    fontSize: 12,
    color: '#111',
    padding: '5mm 5mm',
    maxWidth: 800,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '2px solid #212121',
    paddingBottom: 12,
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: '#212121',
  },
  subtitle: {
    fontSize: 14,
    color: '#555',
    marginTop: 4,
  },
  meta: {
    textAlign: 'right',
    fontSize: 11,
    color: '#555',
  },
  section: {
    paddingTop: 12,
    marginBottom: 20,
    breakInside: 'avoid',
  },
  sectionHeader: {
    fontSize: 13,
    fontWeight: 700,
    color: '#212121',
    borderBottom: '1px solid #ccc',
    paddingBottom: 4,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  subHeader: {
    fontSize: 11,
    fontWeight: 700,
    color: '#444',
    borderBottom: '1px dotted #ddd',
    paddingBottom: 2,
    marginBottom: 6,
    marginTop: 8,
  },
  row: {
    display: 'flex',
    padding: '4px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  label: {
    width: 220,
    flexShrink: 0,
    color: '#555',
    fontWeight: 600,
  },
  value: {
    flex: 1,
    color: '#111',
    wordBreak: 'break-word',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 4,
  },
  th: {
    background: '#f3f3f3',
    padding: '6px 8px',
    textAlign: 'left',
    fontWeight: 700,
    borderBottom: '1px solid #ccc',
    fontSize: 11,
  },
  td: {
    padding: '5px 8px',
    borderBottom: '1px solid #eee',
    verticalAlign: 'top',
    fontSize: 11,
  },
  none: {
    color: '#888',
    fontStyle: 'italic',
    padding: '4px 0',
  },
  footer: {
    marginTop: 30,
    paddingTop: 8,
    borderTop: '1px solid #ccc',
    fontSize: 10,
    color: '#888',
    textAlign: 'center',
  },
};
